/** Viewing stays in the application; files are never sent to third-party viewers. */
export const MAX_RECORD_PREVIEW_BYTES = 30 * 1024 * 1024;
export function safeRecordFileUrl(value) {
  if (typeof value !== 'string') return undefined;
  const url = value.trim();
  if (!url || /[\u0000-\u001f\u007f\\]/.test(url) || url.startsWith('//')) return undefined;
  if (/^data:/i.test(url)) return /^data:[^,]*,/i.test(url) ? url : undefined;
  if (/^blob:https?:\/\//i.test(url)) return url;
  try {
    const parsed = new URL(url, 'https://record.local/');
    return ['https:', 'http:'].includes(parsed.protocol) && !parsed.username && !parsed.password ? url : undefined;
  } catch { return undefined; }
}
export function recordFilePreviewKind(value, name = '') {
  const url = safeRecordFileUrl(value);
  if (!url) return null;
  // For data URLs the MIME, not the filename, decides whether to attempt a preview.
  if (/^data:/i.test(url)) {
    const mime = url.slice(5).split(/[;,]/)[0].toLowerCase();
    if (/^image\/(png|jpe?g|gif|webp|bmp)$/.test(mime)) return 'image';
    return mime === 'application/pdf' ? 'pdf' : null;
  }
  const filename = String(name || url.split(/[?#]/)[0]).toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp)$/.test(filename)) return 'image';
  return /\.pdf$/.test(filename) ? 'pdf' : null;
}
function validatedMime(bytes) {
  const starts = (...values) => values.every((value, index) => bytes[index] === value);
  const ascii = (start, end) => String.fromCharCode(...bytes.slice(start, end));
  if (ascii(0, 5) === '%PDF-') return 'application/pdf';
  if (starts(137, 80, 78, 71, 13, 10, 26, 10)) return 'image/png';
  if (starts(255, 216, 255)) return 'image/jpeg';
  if (['GIF87a', 'GIF89a'].includes(ascii(0, 6))) return 'image/gif';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  if (ascii(0, 2) === 'BM') return 'image/bmp';
  return null;
}
export async function loadRecordPreview(value, name, { signal, fetcher = globalThis.fetch } = {}) {
  const url = safeRecordFileUrl(value);
  const kind = recordFilePreviewKind(url, name);
  if (!url || !kind) throw new Error('Este formato n\u00e3o possui pr\u00e9via. Use Baixar para abrir o arquivo.');
  const response = await fetcher(url, { signal, credentials: 'same-origin', referrerPolicy: 'no-referrer' });
  if (!response.ok) throw new Error('N\u00e3o foi poss\u00edvel carregar o arquivo. Tente novamente ou use Baixar.');
  if (Number(response.headers.get('content-length')) > MAX_RECORD_PREVIEW_BYTES) throw new Error('Arquivo grande para pr\u00e9via. Use Baixar para abri-lo.');
  const blob = await response.blob();
  if (blob.size > MAX_RECORD_PREVIEW_BYTES) throw new Error('Arquivo grande para pr\u00e9via. Use Baixar para abri-lo.');
  const signature = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  const mime = validatedMime(signature);
  if (!mime || (kind === 'pdf') !== (mime === 'application/pdf')) throw new Error('O conte\u00fado n\u00e3o permite uma pr\u00e9via segura. Use Baixar para conferir o arquivo.');
  // Force a verified raster/PDF MIME. HTML, SVG and script documents are never embedded.
  return { kind, blob: new Blob([blob], { type: mime }) };
}
